import * as THREE from "three";
import type { Scenery } from "../../prims/scenery";
import type { Kit } from "./kit";

// The midway's booths (#156). A game stall faces +u: a back wall of prizes,
// side walls and a striped awning out over the front, `wide` across. Put a
// ledge box along its front for the counter you grind. A food stand is a
// kiosk with a serving hatch and a giant ice cream on the roof; a burger
// stand (#233) the same kiosk with a giant burger. Any of them can carry a
// shop sign (`s.sign`), its name on the board.

const PRIZES = ["#ef476f", "#ffd166", "#06d6a0", "#118ab2", "#9b5de5", "#f78c6b"];

export function stall(kit: Kit, s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  const wide = s.wide ?? 4;
  const stripe = s.color ?? "#e63946";
  const wall = "#f4e1c1";
  kit.box(root, 0.15, 2.8, wide, wall, -1.2, 0, 0, "plywood");
  for (const z of [-wide / 2, wide / 2]) kit.box(root, 1.6, 2.8, 0.12, wall, -0.45, 0, z, "plywood");
  // Shelves of prizes (teddy bears: a body and a head).
  for (const y of [1.1, 1.8]) {
    kit.box(root, 0.35, 0.06, wide - 0.3, "#8d5a3b", -1.0, y, 0, "plywood");
    const n = Math.max(2, Math.floor(wide / 0.55));
    for (let i = 0; i < n; i++) {
      const z = -wide / 2 + 0.4 + (i * (wide - 0.8)) / (n - 1);
      const color = PRIZES[(i + (y > 1.5 ? 3 : 0)) % PRIZES.length]!;
      kit.ball(root, 0.16, color, -1.0, y + 0.2, z, "cloth");
      kit.ball(root, 0.11, color, -1.0, y + 0.45, z, "cloth");
    }
  }
  // The awning: stripes running front to back, sloping down to the front.
  const n = Math.max(4, Math.round(wide / 0.5));
  const awning = new THREE.Group();
  for (let i = 0; i < n; i++) {
    const z = -wide / 2 + ((i + 0.5) * wide) / n;
    kit.box(awning, 2.6, 0.06, wide / n, i % 2 ? "#f4f1ea" : stripe, 0, 0, z, "cloth");
    // Scalloped edge.
    kit.box(awning, 0.06, 0.3, wide / n, i % 2 ? stripe : "#f4f1ea", 1.3, -0.3, z, "cloth");
  }
  awning.position.set(0.1, 3.0, 0);
  awning.rotation.z = -0.18;
  root.add(awning);
  // A sign board on top: the shop's name if it has one.
  if (s.sign) {
    const w = Math.min(wide - 0.4, 4.4);
    kit.box(root, 0.12, 0.3, 0.12, "#8d5a3b", -1.2, 3.0, -w / 3);
    kit.box(root, 0.12, 0.3, 0.12, "#8d5a3b", -1.2, 3.0, w / 3);
    kit.sign(root, s.sign, stripe, w, -1.1, 3.3 + (w * 0.25) / 2, 0);
  } else {
    kit.box(root, 0.12, 0.7, Math.min(wide, 3.2), stripe, -1.2, 3.2, 0);
    kit.box(root, 0.14, 0.45, Math.min(wide, 3.2) - 0.3, "#ffd166", -1.18, 3.32, 0);
  }
  return root;
}

/** The kiosk a food or burger stand is: a box with a serving hatch on +u, a roof slab, and its sign over the hatch if it has one. */
function kiosk(kit: Kit, s: Scenery, body: string): THREE.Group {
  const root = new THREE.Group();
  kit.box(root, 2.6, 2.6, 2.6, body, 0, 0, 0);
  kit.box(root, 0.06, 1.0, 1.8, "#3d2c2e", 1.31, 1.1, 0); // the hatch
  kit.box(root, 0.6, 0.08, 2.0, "#f4f1ea", 1.5, 1.05, 0, "plywood"); // its shelf
  kit.box(root, 3.0, 0.25, 3.0, "#f4f1ea", 0, 2.6, 0);
  if (s.sign) kit.sign(root, s.sign, body, 2.6, 1.6, 2.35, 0);
  return root;
}

export function foodStand(kit: Kit, s: Scenery): THREE.Object3D {
  const root = kiosk(kit, s, s.color ?? "#f78c6b");
  // The giant ice cream: a cone and two scoops.
  const cone = new THREE.Mesh(new THREE.ConeGeometry(0.55, 1.6, 12), kit.mat("#d9a066", "plastic"));
  cone.rotation.x = Math.PI;
  cone.position.y = 2.85 + 0.8;
  cone.castShadow = true;
  root.add(cone);
  kit.ball(root, 0.6, "#ff9ebb", 0, 4.6, 0);
  kit.ball(root, 0.5, "#fff1d6", 0, 5.3, 0);
  kit.ball(root, 0.14, "#e63946", 0, 5.85, 0);
  return root;
}

/** A burger stand (#233): the kiosk with a giant burger on the roof, bun, patty, cheese and lettuce, and a flag on a toothpick. */
export function burgerStand(kit: Kit, s: Scenery): THREE.Object3D {
  const root = kiosk(kit, s, s.color ?? "#e63946");
  const y = 2.85;
  kit.cyl(root, 1.05, 0.95, 0.35, "#d98c3f", 0, y, 0, 16); // the bottom bun
  kit.cyl(root, 1.12, 1.12, 0.1, "#7ac74f", 0, y + 0.35, 0, 16, "cloth"); // lettuce
  kit.cyl(root, 1.05, 1.05, 0.32, "#6b3e26", 0, y + 0.45, 0, 16); // the patty
  kit.box(root, 1.9, 0.06, 1.9, "#ffc93c", 0, y + 0.77, 0); // a slice of cheese, its corners hanging over
  const top = kit.ball(root, 1.08, "#e09a4f", 0, y + 0.83, 0); // the top bun, a dome
  top.scale.set(1.08, 0.7, 1.08);
  for (const [x, z] of [[0.3, 0.5], [-0.45, 0.2], [0.1, -0.55], [0.6, -0.1], [-0.2, 0.75]] as const) kit.ball(root, 0.06, "#fff1d6", x, y + 1.45, z);
  kit.rod(root, new THREE.Vector3(0, y + 1.3, 0), new THREE.Vector3(0, y + 2.3, 0), 0.03, "#e8d2a6", "plywood");
  kit.box(root, 0.04, 0.35, 0.55, "#118ab2", 0, y + 1.9, 0.3, "cloth");
  return root;
}
